import { useCrossDealProjects, useProgrammeSummaries, useActiveProfile } from "@/domain/hooks";
import { CrossDealProjectsTable } from "@/screens/shared/CrossDealProjectsTable";
import { CreateProjectAction } from "@/screens/shared/CreateProjectAction";

/**
 * Every individual project across the deals shared with this funder, in one
 * list - reachable directly from the nav (see navSections.ts), not only
 * after clicking into a specific deal. Mirrors FunderDashboardHome.tsx's own
 * isShared construction exactly: a not_shared programme's projects are
 * fetched by the shared hook (both personas call the same one) but filtered
 * out here, never rendered.
 */
export function FunderProjectsPage() {
  const profile = useActiveProfile();
  const programmes = useProgrammeSummaries();
  const { projects, isLoading, failedWorkspaceIds, refetch } = useCrossDealProjects();

  const sharedDeals = programmes.filter((p) => p.workspaceId && p.funderVisibility !== "not_shared");
  const isShared = new Set(sharedDeals.map((p) => p.workspaceId as string));
  const visible = projects.filter((p) => isShared.has(p.workspaceId));

  const deals = sharedDeals.map((p) => ({ workspaceId: p.workspaceId as string, name: p.name }));
  const dealNameByWorkspaceId = Object.fromEntries(deals.map((d) => [d.workspaceId, d.name]));

  // UI convenience only — server enforces stratus_admin/funder_approver
  // (assertCanCreateProjectDirectly), same gate WorkspacePages.tsx's
  // showCreate uses. Deals offered are the same shared-only set this page
  // already renders — a funder can't create into a deal it can't see here.
  const showCreate = profile.group === "oversight" || profile.group === "funder";

  return (
    <div className="space-y-4">
      {showCreate && (
        <div className="flex justify-end">
          <CreateProjectAction deals={deals} />
        </div>
      )}
      <CrossDealProjectsTable
        projects={visible}
        dealNameByWorkspaceId={dealNameByWorkspaceId}
        isLoading={isLoading}
        failedCount={failedWorkspaceIds.length}
        onRetry={refetch}
        emptyTitle="Nothing shared yet"
        emptyBody="Stratus will notify you when project-level detail is ready to share."
      />
    </div>
  );
}
